import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { LoadingButton } from '@mui/lab';
import { Box, Button, Grid } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { GeneralSelectMenu } from 'src/components/general';
import GeneralUpload from 'src/components/general/GeneralUpload';
import { FormProvider, RHFSwitch, RHFTextField } from 'src/components/hook-form';
import { SkeletonForm } from 'src/components/skeleton';
import { networkCreateWheel, networkUpdateWheel, useFetchWheelTypeList } from 'src/network/wheel';
import useAppInfoStore from 'src/store/appInfo';
import useFormsModalStore from 'src/store/forms';
import { generateSelectMenuItems } from 'src/utils';
import { getDefaultValues } from './defaultValues';
import { driver } from 'driver.js';
import ReactDOMServer from 'react-dom/server';
import GeneralDriverTextFiled from 'src/components/general/GeneralDriverTextFiled';

const WheelForm = () => {
  const { modal, closeModal } = useFormsModalStore();

  const { projectId } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const { appInfo } = useAppInfoStore();

  const [wheelTypeList, setWheelTypeList] = useState([]);
  const [selectedWheelType, setSelectedWheelType] = useState('');

  const [amountTypes, setAmountTypes] = useState([]);
  const [selectedAmountType, setSelectedAmountType] = useState('');

  const wheelDetails = modal?.data;

  const defaultValues = getDefaultValues(wheelDetails);
  const isEditMode = !!wheelDetails;

  const methods = useForm({
    // resolver: yupResolver(schema), // todo schema
    defaultValues,
  });

  const {
    reset,
    setValue,
    trigger,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  // ---------------------------------------------------------------

  const onSubmit = async (values, event) => {
    const PAYLOAD = {
      ...values,
      WheelApplicationID: +projectId,
      WheelWheelTypeID: selectedWheelType,
      WheelWaitingHours: +values?.WheelWaitingHours,
      WheelRotateTimes: +values?.WheelRotateTimes,
      WheelFreeChance: +values?.WheelFreeChance,
      WheelCost: +values?.WheelCost,
      WheelEnable: values?.WheelEnable ? 1 : 0,
      WheelTransactionAmountTypeID: selectedAmountType,
      WheelRewardCallbackUrl: values?.WheelRewardCallbackUrl || '',
    };

    try {
      if (isEditMode) {
        await networkUpdateWheel(wheelDetails?.WheelID, PAYLOAD);
        enqueueSnackbar('گردونه موردنظر با موفقیت ویرایش شد');
        if (event.nativeEvent.submitter.name === 'save') {
          closeModal();
          reset();
        }
      } else {
        await networkCreateWheel(PAYLOAD);
        enqueueSnackbar('گردونه موردنظر با موفقیت ایجاد شد');
        if (event.nativeEvent.submitter.name === 'save') {
          closeModal();
        }
        reset();
      }

      await queryClient.invalidateQueries(['wheel-list']);
    } catch (error) {
      console.error(error);
    }
  };

  const { isLoading, data } = useFetchWheelTypeList();

  useEffect(() => {
    const amountTypeList = generateSelectMenuItems(appInfo?.AmountType, 'TransactionAmountTypeLable', 'TransactionAmountTypeID');
    setAmountTypes(amountTypeList);
    setSelectedAmountType(wheelDetails?.WheelTransactionAmountTypeID || amountTypeList[0]?.id);
  }, []);

  useEffect(() => {
    if (data) {
      const wheelTypes = generateSelectMenuItems(data?.data?.items, 'WheelTypeLabel', 'WheelTypeID');
      setWheelTypeList(wheelTypes);
      setSelectedWheelType(wheelDetails?.WheelWheelTypeID || wheelTypes?.[0]?.id);
    }
  }, [data]);

  const driverInstance = driver({
    popoverClass: 'driverjs-theme',
    stagePadding: 0,
    overlayColor: 'transparent',
    onDestroyed: () => {
      document?.activeElement?.blur();
    },
  });

  const renderPreviewJsx = () => (
    <div>
      <img
        src={
          selectedWheelType === 1
            ? '/images/wheel-preview/normal.svg'
            : selectedWheelType === 2
            ? '/images/wheel-preview/jackpot.webp'
            : selectedWheelType === 3
            ? '/images/wheel-preview/full-screen.svg'
            : ''
        }
        className=""
        alt="preview"
      />
    </div>
  );

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box sx={{ p: 3 }}>
            {isLoading ? (
              <Box
                sx={{
                  display: 'grid',
                  columnGap: 2,
                  rowGap: 3,
                  gridTemplateColumns: { xs: 'repeat(1, 1fr)', md: 'repeat(2, 1fr)', lg: 'repeat(2, 1fr)' },
                  width: '100%',
                }}
              >
                <SkeletonForm number={12} />
              </Box>
            ) : (
              <>
                <Box
                  sx={{
                    display: 'grid',
                    columnGap: 2,
                    rowGap: 3,
                    gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
                  }}
                >
                  <RHFTextField name="WheelLabel" type="text" label="عنوان" />

                  <GeneralSelectMenu
                    label="نوع گردونه"
                    name="WheelWheelTypeID"
                    options={wheelTypeList}
                    value={selectedWheelType}
                    onChangeValue={setSelectedWheelType}
                    hasButton={true}
                    btnTitle="پیشنمایش"
                    onClick={(e) =>
                      driverInstance.highlight({
                        element: e.target,
                        popover: {
                          title: 'پیشنمایش گردونه انتخاب شده',
                          description: ReactDOMServer.renderToString(renderPreviewJsx()),
                        },
                      })
                    }
                  />

                  <GeneralUpload
                    label="تصویر گردونه"
                    id="wheel_backdrop"
                    name="WheelBackdrop"
                    afterChangeAction={(res) => {
                      setValue('WheelBackdrop', res);
                      trigger('WheelBackdrop');
                    }}
                  />

                  <RHFTextField name="WheelWaitingHours" type="text" label="مدت زمان انتظار (ساعت)" />

                  <RHFTextField name="WheelRewardCallbackUrl" type="text" label="دامنه تامین جوایز" />

                  <GeneralDriverTextFiled
                    name="WheelRotateTimes"
                    type="text"
                    label="تعداد گردش گردونه"
                    helperDesc={`تعداد دفعاتی که گردونه به دور خود میچرخد .`}
                  />
                  <GeneralDriverTextFiled
                    helperDesc={`تعداد دفعات رایگانی که کاربر میتواند از گردونه استفاده کند. `}
                    name="WheelFreeChance"
                    type="text"
                    label="تعداد دفعات قابل گردش رایگان"
                  />

                  <GeneralSelectMenu
                    label="نوع واحد پرداخت"
                    name="WheelTransactionAmountTypeID"
                    options={amountTypes}
                    value={selectedAmountType}
                    onChangeValue={setSelectedAmountType}
                  />
                  <RHFTextField name="WheelCost" type="text" label="هزینه گرداندن" />
                </Box>

                <Box
                  sx={{
                    display: 'grid',
                    columnGap: 2,
                    rowGap: 3,
                    gridTemplateColumns: { xs: 'repeat(1, 1fr)' },
                    mt: 3,
                  }}
                >
                  <RHFTextField name="WheelDescription" multiline rows={6} label="توضیحات" />
                  <RHFSwitch name="WheelEnable" labelPlacement="end" label="فعال کردن گردونه" />
                </Box>

                <div className="flex items-center gap-4 mt-6">
                  <LoadingButton type="submit" variant="contained" name="save" loading={isSubmitting}>
                    ذخیره
                  </LoadingButton>
                  <LoadingButton type="submit" variant="contained" color="info" name="saveAndContinue" loading={isSubmitting}>
                    {isEditMode ? 'ذخیره و ادامه ویرایش' : 'ذخیره و ایجاد بعدی'}
                  </LoadingButton>
                  <Button onClick={closeModal} variant="contained" color="error">
                    انصراف
                  </Button>
                </div>
              </>
            )}
          </Box>
        </Grid>
      </Grid>
    </FormProvider>
  );
};

export default WheelForm;
